import { useState, useEffect } from 'react';
import { Settings, Copy, CheckCircle, AlertCircle, Webhook, Eye, EyeOff } from 'lucide-react';
import { api } from '../../utils/api';

export default function WhatsAppConfig() {
  const [cfg, setCfg] = useState({ verify_token: '', phone_number_id: '', waba_id: '', access_token: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState('');

  const webhookUrl = `${window.location.origin.replace(':3000', ':3001')}/webhook`;
  const webhookUrlAlt = `https://connect.vasavyavidyalayam.in/webhook`;

  useEffect(() => {
    api.get('/api/whatsapp/webhook-config').then(d => {
      if (d) setCfg(c => ({ ...c, ...d }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.put('/api/whatsapp/webhook-config', cfg);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e.message || 'Failed to save'); }
    setSaving(false);
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: '#25D366' }}>
          <Settings size={20} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800">WhatsApp Webhook Configuration</h1>
          <p className="text-sm text-gray-500">Meta Cloud API — webhook setup for receiving messages</p>
        </div>
      </div>

      {/* Step 1 — Webhook URL */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold" style={{ background: '#25D366' }}>1</span>
          <h2 className="font-semibold text-gray-800">Webhook URL</h2>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          In your <strong>Meta Developer Console → WhatsApp → Configuration</strong>, set the Webhook URL to:
        </p>
        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3 border">
          <code className="text-sm text-green-700 flex-1 break-all">{webhookUrlAlt}</code>
          <button onClick={() => copyText(webhookUrlAlt, 'url')}
            className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors">
            {copied === 'url' ? <CheckCircle size={16} className="text-green-500" /> : <Copy size={16} />}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">Subscribe to: <strong>messages</strong></p>
      </div>

      {/* Step 2 — Verify Token */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold" style={{ background: '#25D366' }}>2</span>
          <h2 className="font-semibold text-gray-800">Verify Token</h2>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Set the same token here and in Meta Developer Console. This proves you own the webhook.
        </p>
        <div className="relative">
          <input
            type={showToken ? 'text' : 'password'}
            value={cfg.verify_token}
            onChange={e => setCfg(c => ({ ...c, verify_token: e.target.value }))}
            placeholder="e.g. vasavya_webhook_2024"
            className="w-full border rounded-lg px-4 py-2.5 text-sm pr-20 focus:outline-none focus:ring-2 focus:ring-green-300" />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <button onClick={() => setShowToken(s => !s)} className="text-gray-400 hover:text-gray-600">
              {showToken ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
            <button onClick={() => copyText(cfg.verify_token, 'vt')} className="text-gray-400 hover:text-gray-600">
              {copied === 'vt' ? <CheckCircle size={15} className="text-green-500" /> : <Copy size={15} />}
            </button>
          </div>
        </div>
      </div>

      {/* Step 3 — API Credentials */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold" style={{ background: '#25D366' }}>3</span>
          <h2 className="font-semibold text-gray-800">Meta API Credentials</h2>
          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">Required for sending replies</span>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number ID</label>
            <input value={cfg.phone_number_id}
              onChange={e => setCfg(c => ({ ...c, phone_number_id: e.target.value }))}
              placeholder="From Meta → WhatsApp → Getting Started"
              className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">WhatsApp Business Account ID (WABA ID)</label>
            <input value={cfg.waba_id}
              onChange={e => setCfg(c => ({ ...c, waba_id: e.target.value }))}
              placeholder="From Meta Business Manager"
              className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Permanent Access Token</label>
            <input type="password" value={cfg.access_token}
              onChange={e => setCfg(c => ({ ...c, access_token: e.target.value }))}
              placeholder="System User permanent token from Meta Business Suite"
              className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" />
            <p className="text-xs text-gray-400 mt-1">Generate a <strong>System User token</strong> (never-expiring) from Meta Business Suite → System Users</p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 rounded-2xl border border-blue-100 p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Webhook size={16} className="text-blue-600" />
          <h3 className="font-semibold text-blue-800 text-sm">How it works</h3>
        </div>
        <ul className="space-y-1.5 text-xs text-blue-700">
          <li>• When a parent replies to a WhatsApp message, Meta sends it to your webhook URL</li>
          <li>• The server receives and stores the message in the <strong>Inbox</strong></li>
          <li>• You can view and reply from the <strong>Conversations</strong> page</li>
          <li>• Outbound replies are sent via Meta Cloud API using your access token</li>
          <li>• Delivery and read receipts are tracked automatically</li>
        </ul>
      </div>

      {/* Save */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-4 py-3 mb-4 text-sm">
          <AlertCircle size={15} />{error}
        </div>
      )}
      {saved && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-lg px-4 py-3 mb-4 text-sm">
          <CheckCircle size={15} />Configuration saved successfully!
        </div>
      )}
      <button onClick={save} disabled={saving}
        className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-opacity disabled:opacity-50"
        style={{ background: '#25D366' }}>
        {saving ? 'Saving…' : 'Save Configuration'}
      </button>
    </div>
  );
}
